import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { trackerCoachRequest } from '../tracker/trackerCoachClient';

// Only explicit answer-check/review actions enqueue updates. Restoring a saved
// answer and synchronizing an old daily-plan result must never build a streak.
export default function usePracticeActivity(paperId) {
    const { user, userData } = useAuth() || {};
    const allowed = userData?.role === 'student' && userData.assignedPapers?.includes(paperId);
    const scope = JSON.stringify([user?.uid, paperId, Boolean(allowed)]);
    const currentScope = useRef(scope), generation = useRef(0), mounted = useRef(true);
    const jobs = useRef(new Map()), completed = useRef(new Set());
    const [state, setState] = useState({ scope, pending: 0, failed: 0 });
    currentScope.current = scope;
    useEffect(() => {
        generation.current++; jobs.current.clear(); completed.current.clear();
        setState({ scope, pending: 0, failed: 0 });
    }, [scope]);
    useEffect(() => { const lifecycle = generation; mounted.current = true; return () => { mounted.current = false; lifecycle.current++; }; }, []);
    const publish = useCallback(() => {
        if (!mounted.current || currentScope.current !== scope) return;
        const statuses = [...jobs.current.values()].map(job => job.status);
        setState({ scope, pending: statuses.filter(status => status === 'pending').length, failed: statuses.filter(status => status === 'failed').length });
    }, [scope]);
    const send = useCallback(async job => {
        if (!mounted.current || currentScope.current !== scope || job.generation !== generation.current || job.status === 'pending') return;
        job.status = 'pending'; publish();
        try {
            await trackerCoachRequest('attempt', { paperId, questionId: job.questionId, resultVersion: job.resultVersion });
            if (!mounted.current || currentScope.current !== scope || job.generation !== generation.current || jobs.current.get(job.key) !== job) return;
            jobs.current.delete(job.key); completed.current.add(job.key);
        } catch {
            if (!mounted.current || currentScope.current !== scope || job.generation !== generation.current || jobs.current.get(job.key) !== job) return;
            job.status = 'failed';
        } finally { if (mounted.current && currentScope.current === scope && job.generation === generation.current) publish(); }
    }, [paperId, scope, publish]);
    const recordAttempt = useCallback((questionId, resultVersion) => {
        if (!user?.uid || !allowed || currentScope.current !== scope || !questionId || !Number.isFinite(resultVersion) || resultVersion <= 0) return;
        const key = `${questionId}:${resultVersion}`;
        if (jobs.current.has(key) || completed.current.has(key)) return;
        // A later saved result supersedes failed older versions of this question.
        jobs.current.forEach(previous => { if (previous.questionId === questionId && previous.resultVersion < resultVersion) jobs.current.delete(previous.key); });
        const job = { key, questionId, resultVersion, generation: generation.current, status: 'queued' };
        jobs.current.set(key, job); send(job);
    }, [user?.uid, allowed, scope, send]);
    const retry = useCallback(() => { jobs.current.forEach(job => { if (job.status === 'failed') send(job); }); }, [send]);
    return { ...(state.scope === scope ? state : { pending: 0, failed: 0 }), recordAttempt, retry };
}
